import React from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import { VscSend } from 'react-icons/vsc';

import Tooltip from '../../../../../controls/common/tooltip';
import { MarkdownBody } from '../../../common/markdown';

import { ANSWER_ROWS } from '../../components/autoGrow';
import GrowTextarea from '../../components/growTextarea';
import { inFlightQuestions } from '../../domain/cellObjects';
import QuestionInput from './questionInput';

/**
 * CellInput — the control pinned at the foot of a cell's thread, and the draft behind it.
 *
 * **What is pinned is small: a field, or just a button.** A question's own bulk — its text, its
 * options, their descriptions — is content, and content scrolls with the thread it belongs to. A
 * two-question prompt with five options each is a screenful; pinned at the foot it pushed the whole
 * thread off the top of the pane, so the story that explains the question could not be read while
 * answering it. The rows are drawn on the record that raised them (`inputControl`), in place, and
 * only the commit control stays put.
 *
 * So the foot has three shapes, and the question decides which:
 *
 *  - **one free-text question** — the field IS the answer, and it is small. Field, then button.
 *  - **a gate** — the rows in the thread are the answer, one per edge; the field here is a NOTE
 *    about the choice, optional, and the prose the next run reads through `{{carryover}}`.
 *  - **anything else with rows** — the answering happens up in the thread. Button alone.
 *
 * `useAnswerDraft` is what makes that split possible: the draft lives above both, so the rows in the
 * thread and the button at the foot are two views of one answer. It is also the only place that
 * knows what a request ASKS — `inFlightQuestions`, never a second reading — including the case a
 * hand-rolled mapping gets wrong: an APPROVAL is two fixed choices and NO free text, because the
 * worker reads anything outside seven affirmative words as a denial, so a typed "yes, but only the
 * first one" is a Deny wearing a yes.
 *
 * A cell with no request takes no input, and both render nothing. Every status publishes one —
 * `working` a steering box, `inactive`/`failed`/`completed` a resume box, `input_needed` whatever an
 * agent or a gate actually asked — so there is no table here saying which statuses take input.
 */

/**
 * The intent the button carries, and the ones behind the caret.
 *
 * `picked` is the verb somebody chose from the caret, if they have; without one the button carries
 * the request's default. **The caret lists everything the button is NOT** — computed against the
 * intent actually on the button rather than against the default, because otherwise choosing from
 * the menu left the chosen verb listed inside its own menu and the one it replaced unreachable.
 * On a two-verb request that meant a person could pick `Complete` and never get back to `Answer`.
 */
export function intentsOf(request, picked = null) {
  const intents = ((request || {}).intents || []).filter(intent => intent && intent.verb);
  if (intents.length === 0) return { chosen: null, others: [] };
  const chosen = intents.find(intent => intent.verb === picked)
    || intents.find(intent => intent.default)
    || intents[0];
  return { chosen, others: intents.filter(intent => intent !== chosen) };
}

/**
 * What identifies the question being answered, so a new one starts a new draft. A run can ask twice
 * on one record with the cursor unmoved, so the prompt id and the question text are both part of it.
 */
function draftKey(request, questions) {
  const source = request || {};
  const prompt = (source.worker_prompt || {}).prompt_id || '';
  return [source.mode, source.step, source.run, prompt,
          questions.map(question => question.prompt).join(' ')].join('|');
}

/**
 * The answer being composed, shared by the rows in the thread and the button at the foot.
 *
 * Held above both because it is one answer: picking an option is drawn on the record that offered
 * it, and committing is a control that never scrolls away.
 */
export function useAnswerDraft(request) {
  const questions = inFlightQuestions(request);
  const [verb, setVerb] = React.useState(null);
  const { chosen, others } = intentsOf(request, verb);
  const [picked, setPicked] = React.useState({});
  const [typed, setTyped] = React.useState({});
  // What a person adds AT a gate. Not an answer — the answer is the edge they picked — so it is one
  // field for the whole request rather than one per question, and it is never required.
  const [comment, setComment] = React.useState('');

  // A different question is a different answer. Without this, a run asking a second thing on the
  // same record would inherit the first one's choices — already lit, and already valid to send.
  const key = draftKey(request, questions);
  const [drafted, setDrafted] = React.useState(key);
  if (drafted !== key) {
    setDrafted(key);
    setPicked({});
    setTyped({});
    setComment('');
    setVerb(null);
  }

  const answerFor = (index) => {
    const question = questions[index] || {};
    // What the bridge READS, where the two differ: an approval's buttons say Approve and Deny and
    // post `approve` and `deny`, because those are the words it resolves. `text` is that word;
    // `label` is what a person reads.
    const chosen = (picked[index] || []).map(label => {
      const choice = (question.choices || []).find(entry => entry.label === label);
      return (choice && choice.text) || label;
    });
    // Free text where the question allows it — never on an approval, whose two labels are the whole
    // of what the worker's bridge can read back.
    const own = question.freeText ? (typed[index] || '').trim() : '';
    if (!own) return chosen;
    // **`many` ADDS, `one` REPLACES.** On a multi-select the typed row is one more option and the
    // answer is all of them; on a single-select the answer is one value, so typing is a person
    // changing their mind and the pick goes. `type` unlights the row to match — an option still lit
    // beside a filled field said the answer was both.
    return question.mode === 'many' ? chosen.concat(own) : [own];
  };

  // **What the foot is: a field, or a button.** One free-text question is small enough to pin, and
  // is the only shape that is. Anything with options, or with a second question, is rows — and rows
  // belong in the thread.
  const lone = questions.length === 1 && questions[0].choices.length === 0;
  // A gate: its answer names an edge, so the rows are the whole of the answer and the field beside
  // the button is a comment on it.
  const routing = questions.some(question => question.routes);

  return {
    questions,
    picked,
    typed,
    lone,
    routing,
    comment,
    setComment,
    ready: !!chosen && questions.length > 0,
    others,
    // **An intent that does not ANSWER is not held by the questions.** `complete` ends the cell, so
    // the question beside it is moot — without this a person at a gate would have to pick a route
    // they are not taking before the control would let them say the cell is finished. Requiredness
    // belongs to answering, not to every verb the control can send, and the request says which is
    // which (`request.py: _intent`).
    intent: chosen,
    setVerb,
    // Per QUESTION: the ones that must be answered on their own.
    //
    // Plus one rule on the REQUEST — **a parked run needs to be told something.** An `in_flight`
    // question is one an agent stopped mid-turn to ask, and the worker discards an inbound item with
    // no text: an empty answer to it posts a record nothing can act on and leaves the run waiting on
    // a question that now looks answered. That is the shape that stranded a live cell. It is a rule
    // about the whole prompt rather than each question, because anything said unparks it — an
    // "anything else?" left blank beside an answered one is fine.
    //
    // A GATE is not this. Nothing is parked on it; an empty answer routes its implied exit perfectly
    // well, and its comments are optional by design.
    unanswered: (chosen || {}).answers !== false
      && (questions.some(
        (question, index) => question.required && answerFor(index).length === 0)
        || ((request || {}).mode === 'in_flight'
            && questions.every((question, index) => answerFor(index).length === 0))),
    pick: (index, choice) => {
      setPicked(current => ({
        ...current,
        [index]: questions[index].mode === 'many'
          ? toggle(current[index] || [], choice.label)
          : [choice.label],
      }));
      // The mirror of the rule below, in the direction a person is just as likely to go: picking an
      // option on a single-select after typing means the option, so the field empties rather than
      // silently outranking the row that just lit.
      if (questions[index].mode !== 'many') setTyped(current => ({ ...current, [index]: '' }));
    },
    type: (index, value) => {
      setTyped(current => ({ ...current, [index]: value }));
      // A single-select holds ONE answer. An option left lit while somebody types their own is the
      // screen saying two things, and the typed one is what would be sent.
      if (value.trim() && questions[index].mode !== 'many') {
        setPicked(current => ({ ...current, [index]: [] }));
      }
    },
    /** The answer as it will be posted. */
    build: () => {
      // Nothing structural for a verb that does not answer: `complete` ends the cell, and an
      // answer posted beside it would be a decision nobody made about a question nobody is waiting
      // on. Its prose is whatever the person typed, which is a note about ending it.
      if ((chosen || {}).answers === false) {
        return { answers: [], text: comment.trim() || (typed[0] || '').trim() };
      }
      const answers = questions
        .map((question, index) => ({ question: question.prompt, answer: answerFor(index) }))
        .filter(entry => entry.answer.length > 0);
      // The prose is the summary a person reads back in the feed: what they answered, in order. The
      // questions are on screen directly above it, so the answers alone read as a reply rather than
      // as a transcript — and with one free-text question the prose IS the answer, written once.
      //
      // It is never empty when something was answered, which is why this is derived rather than read
      // off the field: an approval has no field at all, and a bare `intent · answer` head with
      // nothing under it is a person's own decision missing from their own feed.
      //
      // At a GATE the comment IS the prose, because the answer itself is a label already carried
      // structurally — and it is the comment that a later run reads through `{{carryover}}`, where
      // the label alone says only which way the run went.
      const said = comment.trim();
      if (routing && said) return { answers, text: said };
      return { answers, text: answers.map(entry => entry.answer.join(', ')).join('\n') };
    },
    clear: () => { setPicked({}); setTyped({}); setComment(''); },
  };
}

export default function CellInput({ draft, busy, onSend, carried }) {
  if (!draft || !draft.ready) return null;
  const { questions, lone, routing, intent, others, unanswered } = draft;
  const blocked = busy || unanswered;

  const send = () => {
    if (blocked) return;
    const { answers, text } = draft.build();
    onSend(intent.verb, text, answers);
    draft.clear();
  };

  // Enter commits, Shift+Enter is a newline — the contract every field in this widget keeps, and the
  // reason the button's tooltip says so rather than the field carrying a hint of its own. What it
  // commits is what the button says, so the key never does something the screen did not.
  const onKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    send();
  };

  // **The foot says the question only when nothing else on screen does.** A question an agent or a
  // gate raised is drawn on the record that raised it — that is where it belongs, in time order,
  // among the things that led to it — so saying it again over the field is the same question twice.
  // `carried` is the pane's answer to "is a record carrying this one?", because the pane is what
  // knows. **It is stated in the negative deliberately**: the default is to say the question, so a
  // caller that forgets says it twice — untidy — rather than losing it, which is a question on
  // screen NOWHERE.
  //
  // It matters in two cases: a request the cell publishes about ITSELF (resume, restart, steer),
  // which has no record anywhere, and a raised question whose record has not loaded — an agent's
  // SECOND question on a page that only ever saw the first.
  const titled = !carried;
  // One short phrase reads better as the field's own placeholder than as a line above it. Several
  // paragraphs do not — that is what turned this control into a wall of grey — so a long one is a
  // heading and the field says `Your answer…`.
  const asPlaceholder = titled && lone && questions[0].prompt.length <= 80;

  const commit = (
    <span className="mc-composer-commit">
      <Tooltip text={`${intent.label} (Shift+Enter for newline)`}>
        <Button
          className={`bh-composer-send bh-composer-send-labelled${
            others.length > 0 ? ' bh-composer-send-has-split' : ''}`}
          disabled={blocked} onClick={send}>
          {/* Sized by the icon rather than by the button, so the glyph is the thread's glyph exactly
              while the label beside it reads at 13. */}
          <VscSend size={15} />{intent.label}
        </Button>
      </Tooltip>
      {/* `drop="up"`: this control lives at the bottom of a pane, the way the thread composer's own
          menu does, so a list dropped downwards would open off the surface. */}
      {others.length > 0 && (
        <Dropdown drop="up" alignRight>
          <Dropdown.Toggle split
            className="bh-composer-send bh-composer-send-split"
            disabled={busy} aria-label="Choose what to do"
            id="mc-cell-input-verb" />
          <Dropdown.Menu>
            {others.map(entry => (
              <Dropdown.Item key={entry.verb} onClick={() => draft.setVerb(entry.verb)}>
                {entry.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </span>
  );

  // **Rows go in the thread when the thread has them — and here when it does not.** A record
  // carrying the question draws its own rows, so all that is left to pin is the button. With no
  // record on screen there is nowhere else for them to be, and bulk at the foot beats a question
  // that cannot be answered at all.
  //
  // A GATE keeps a field even so, and it is not an answer: the rows ARE the answer, one per edge the
  // run can take. This is what a person wants to say ABOUT the choice — the note the next run reads
  // through `{{carryover}}`, where a bare label says only which way it went.
  if (!lone && carried) {
    if (!routing) return <div className="mc-composer mc-composer-bare">{commit}</div>;
    return (
      <div className="mc-composer">
        <div className="bh-composer-shell">
          <GrowTextarea
            rows={1}
            maxRows={ANSWER_ROWS}
            className="bh-composer-textarea"
            placeholder="Add a note (optional)…"
            value={draft.comment}
            disabled={busy}
            onChange={draft.setComment}
            onKeyDown={onKeyDown}
          />
          {commit}
        </div>
      </div>
    );
  }

  return (
    <div className="mc-composer">
      {titled && !asPlaceholder && lone && (
        <MarkdownBody text={questions[0].prompt} className="mc-question" />
      )}
      {!lone && questions.map((question, index) => (
        <QuestionInput
          key={index}
          question={question}
          picked={draft.picked[index] || []}
          typed={draft.typed[index] || ''}
          busy={busy}
          headed
          onPick={choice => draft.pick(index, choice)}
          onType={value => draft.type(index, value)}
        />
      ))}
      <div className="bh-composer-shell">
        {lone && <GrowTextarea
          rows={1}
          maxRows={ANSWER_ROWS}
          className="bh-composer-textarea"
          placeholder={asPlaceholder ? questions[0].prompt : 'Your answer…'}
          value={draft.typed[0] || ''}
          disabled={busy}
          onChange={value => draft.type(0, value)}
          onKeyDown={onKeyDown}
        />}
        {commit}
      </div>
    </div>
  );
}

/** Multi-select: a second click on the same option takes it back off. */
function toggle(current, label) {
  return current.includes(label)
    ? current.filter(value => value !== label)
    : current.concat(label);
}
